"use client";

// ===================================
// ENHANCED PUBLIC CHAT WIDGET WITH AGENTIC AUTH SUPPORT
// src/components/PublicChatWidget.tsx
// ===================================
import type React from "react";
import { useState, useEffect, useRef } from "react";
import PublicChat from "./PublicChat";
import { sessionManager } from "../services/api";

import {
  MessageSquare,
  X,
  Minimize2,
  Maximize2,
  Bot,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import AiChatIcon from "../assets/aiChatIcon.png";

interface PublicChatWidgetProps {
  position?: "bottom-right" | "bottom-left";
  theme?: "blue" | "purple" | "green";
  customTitle?: string;
  showBadge?: boolean;
  className?: string;
  enableAgenticAuth?: boolean;
  onAuthSuccess?: (principalId: string) => void;
}

const PublicChatWidget: React.FC<PublicChatWidgetProps> = ({
  position = "bottom-right",
  theme = "blue",
  customTitle = "Customer Support",
  showBadge = true,
  className = "",
  enableAgenticAuth = true,
  onAuthSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [hasAgenticNotice, setHasAgenticNotice] = useState(false);
  const [isAgenticVerified, setIsAgenticVerified] = useState(false);
  const [chatKey, setChatKey] = useState(0);

  const chatInitialized = useRef(false);
  const sessionRef = useRef<string | null>(null);

  // Theme configurations
  const themeConfig = {
    blue: {
      primary: "from-blue-600 to-purple-600",
      hover: "hover:from-blue-700 hover:to-purple-700",
      text: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
    },
    purple: {
      primary: "from-purple-600 to-pink-600",
      hover: "hover:from-purple-700 hover:to-pink-700",
      text: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
    },
    green: {
      primary: "from-green-600 to-emerald-600",
      hover: "hover:from-green-700 hover:to-emerald-700",
      text: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
    },
  };

  const currentTheme = themeConfig[theme];

  // Position classes
  const positionClasses = {
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  };

  useEffect(() => {
    const isVerified = sessionManager.isAgenticVerified();
    setIsAgenticVerified(isVerified);
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleCloseChat();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Only prevent body scroll when chat is open and not minimized
      if (!isMinimized) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isMinimized]);

  // Clean up body scroll on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Reset chat when opening for better initialization
  useEffect(() => {
    if (isOpen && !chatInitialized.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setChatKey((prev) => prev + 1);
        chatInitialized.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleChatReady = () => {
      chatInitialized.current = true;
    };

    const handleNewMessage = () => {
      if (isMinimized) {
        setUnreadCount((prev) => prev + 1);
        setHasNewMessage(true);
      }
    };

    const handleAgenticNotice = (e: CustomEvent) => {
      setHasAgenticNotice(true);
      if (isMinimized) {
        setUnreadCount((prev) => prev + 1);
      }

      // Store the session ID from the event for tracking
      if (e.detail?.sessionId) {
        sessionRef.current = e.detail.sessionId;
      }

      // Let PublicChatPage handle the auth modal
    };

    const handleVerificationChange = () => {
      const isVerified = sessionManager.isAgenticVerified();
      setIsAgenticVerified(isVerified);
      setHasAgenticNotice(false); // Clear notice when verified

      if (isVerified && onAuthSuccess) {
        const principalId = sessionManager.getPrincipalId() || "verified";
        onAuthSuccess(principalId);
      }
    };

    const handleSessionCreated = (e: CustomEvent) => {
      if (e.detail?.sessionId) {
        sessionRef.current = e.detail.sessionId;
      }
    };

    const handleNotification = (event: any) => {
      const { type, title, message } = event.detail;
    };

    // Custom events from PublicChat component
    window.addEventListener("chatReady", handleChatReady);
    window.addEventListener("newChatMessage", handleNewMessage);
    window.addEventListener(
      "agenticNotice",
      handleAgenticNotice as EventListener
    );
    window.addEventListener(
      "agenticVerificationChanged",
      handleVerificationChange
    );
    window.addEventListener(
      "publicChatSessionCreated",
      handleSessionCreated as EventListener
    );
    window.addEventListener("showNotification", handleNotification);

    return () => {
      window.removeEventListener("chatReady", handleChatReady);
      window.removeEventListener("newChatMessage", handleNewMessage);
      window.removeEventListener(
        "agenticNotice",
        handleAgenticNotice as EventListener
      );
      window.removeEventListener(
        "agenticVerificationChanged",
        handleVerificationChange
      );
      window.removeEventListener(
        "publicChatSessionCreated",
        handleSessionCreated as EventListener
      );
      window.removeEventListener("showNotification", handleNotification);
    };
  }, [isMinimized, enableAgenticAuth, onAuthSuccess]);

  const handleToggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      // Opening chat
      setUnreadCount(0);
      setHasNewMessage(false);
      setIsMinimized(false);
      chatInitialized.current = false; // Reset initialization flag
      document.body.style.overflow = "hidden";
    } else {
      // Closing chat
      handleCloseChat();
    }
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
    chatInitialized.current = false;
    document.body.style.overflow = "unset";
    // Reset chat component on close
    setTimeout(() => {
      setChatKey((prev) => prev + 1);
    }, 300); // Wait for animation to complete
  };

  const handleMinimizeChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newIsMinimized = !isMinimized;
    setIsMinimized(newIsMinimized);

    // Manage body scroll based on minimized state
    if (newIsMinimized) {
      document.body.style.overflow = "unset";
    } else {
      document.body.style.overflow = "hidden";
    }
  };

  // Floating widget button
  if (!isOpen) {
    return (
      <div className={`fixed ${positionClasses[position]} z-50 ${className}`}>
        {/* Chat Widget Button */}
        <button
          onClick={handleToggleChat}
          className={`relative group w-14 h-14 bg-gradient-to-br ${currentTheme.primary} ${currentTheme.hover} text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-200`}
          aria-label="Open chat support"
        >
          <img src={AiChatIcon} alt="chat icon" className="bot-image" />

          {/* Unread count badge */}
          {unreadCount > 0 && (
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          )}

          {/* New message indicator */}
          {hasNewMessage && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
          )}

          {hasAgenticNotice && !isAgenticVerified && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-3 h-3 text-white" />
            </div>
          )}

          {isAgenticVerified && (
            <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
          )}

          <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
            {isAgenticVerified ? `${customTitle} - Enhanced` : customTitle}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </button>

        {/* Pulsing ring animation */}
        {showBadge && (
          <div className="absolute inset-0 rounded-full animate-pulse pointer-events-none">
            <div
              className={`w-14 h-14 rounded-full bg-gradient-to-br ${currentTheme.primary} opacity-20 animate-ping`}
            ></div>
          </div>
        )}
      </div>
    );
  }

  // Modal overlay and chat interface
  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Backdrop overlay - only show when not minimized */}
      {!isMinimized && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
          onClick={handleCloseChat}
        />
      )}

      {/* Chat container */}
      <div
        className={`fixed transition-all duration-300 ${
          isMinimized
            ? `${positionClasses[position]} w-80` // Positioned like floating widget when minimized
            : "inset-0 flex items-center justify-center p-4 sm:p-6 lg:p-8" // Centered when expanded
        }`}
      >
        <div
          className={`relative bg-white rounded-xl shadow-2xl transform transition-all duration-300 ${
            isMinimized
              ? "w-full h-auto"
              : "w-full max-w-4xl h-full max-h-[600px]"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Chat Header */}
          <div
            className={`bg-[#8b3dff] text-white p-4 rounded-t-xl flex items-center justify-between`}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg flex items-center space-x-2">
                  <span>{customTitle}</span>
                  {isAgenticVerified && (
                    <CheckCircle className="w-4 h-4 text-green-300" />
                  )}
                </h3>
                <p className="text-sm text-blue-100">
                  {isMinimized
                    ? "Click to expand"
                    : chatInitialized.current
                    ? isAgenticVerified
                      ? "Enhanced support ready"
                      : "We typically reply instantly"
                    : "Starting up..."}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Minimize/Maximize Button */}
              <button
                onClick={handleMinimizeChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label={isMinimized ? "Maximize chat" : "Minimize chat"}
              >
                {isMinimized ? (
                  <Maximize2 className="w-5 h-5" />
                ) : (
                  <Minimize2 className="w-5 h-5" />
                )}
              </button>

              {/* Close Button */}
              <button
                onClick={handleCloseChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <div
              className="flex flex-col"
              style={{ height: "calc(600px - 72px)" }}
            >
              <PublicChat
                key={chatKey} // Force re-render when chatKey changes
                isEmbedded={true}
                className="flex-1 rounded-none border-none"
                onSessionCreated={(sessionId) => {
                  sessionRef.current = sessionId;
                }}
                onAgenticNotice={(data) => {
                  setHasAgenticNotice(true);
                  // Let the event bubble up to PublicChatPage for modal handling
                }}
              />
            </div>
          )}

          {/* Minimized state with expand hint */}
          {isMinimized && (
            <div
              className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-xl cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={handleMinimizeChat}
            >
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Chat minimized - Click to expand</span>
                <Maximize2 className="w-4 h-4" />
              </div>
              {unreadCount > 0 && (
                <div className="mt-2 text-xs text-blue-600 font-medium">
                  {unreadCount} new message{unreadCount > 1 ? "s" : ""}
                </div>
              )}
              {hasAgenticNotice && !isAgenticVerified && (
                <div className="mt-1 text-xs text-yellow-600 font-medium flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Authentication required - check main page
                </div>
              )}
              {isAgenticVerified && (
                <div className="mt-1 text-xs text-green-600 font-medium flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Enhanced access active
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicChatWidget;
